import {
  Children,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type Ref,
} from 'react';
import { replaceCommentEmojiShortcodes, type BoardMemberWithHistory } from '@molt/shared';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { profileName } from '@/components/Avatar';
import { commentMentionSegments } from '@/lib/comments';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * Locally declared so the module stays decoupled from the interface-language
 * context, matching `CommentTranslate` (`lib/comments.ts:37-41`). `FE-80.1`
 * routes the mention chip's accessible name through it; the component reads
 * the real `t` from `useInterfaceLanguage()` and threads it down both module
 * hops, so neither helper reaches for the context itself.
 */
type MarkdownTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

/**
 * Renders stored Markdown for two columns — the card description
 * (`FR-CARD-003`) and every comment body (`FR-COLLAB-001`) — through ONE
 * sanitiser allow-list (`docs/design/stage2/card-back.md`'s `MarkdownBody`
 * row: "the sanitiser allow-list must be one list or the two surfaces
 * disagree about what an image is"). `ALLOWED_MARKDOWN_ELEMENTS` below is
 * that one list; both callers get it by using this component, never by
 * copying it.
 *
 * **Where the sanitisation actually happens.** `react-markdown` does not
 * interpret raw HTML embedded in the Markdown source unless the `rehype-raw`
 * plugin is added — it is not installed, so a `<script>` or an `<iframe>`
 * pasted into a comment is inert by construction, not by a filter this file
 * has to keep correct. `allowedElements` narrows the constructs Markdown
 * ITSELF can still produce (GFM tables, task-list checkboxes, images) to the
 * ones the product wants. `urlTransform` is left at react-markdown's default,
 * which already refuses non-`http(s)`/`mailto`/`tel`/relative schemes on
 * both `href` and `src` — so no `javascript:` link is left to a second layer
 * added here.
 *
 * **It renders; it does not edit.** The editing surface is
 * `card-back/RichDescriptionEditor.tsx` (`FE-14.2`) — a plain textarea over
 * Markdown SOURCE plus a formatting toolbar that writes the syntax — and it
 * calls this component for its live preview, so the preview and the saved
 * read view can never disagree about how a construct renders. (This paragraph
 * used to cite `FR-CARD-003`'s WYSIWYG editor as the thing deliberately not
 * built. The 2026-09-06 corpus ruling deleted that sentence from the
 * requirement: there is one editing mode and no preference to choose between
 * two. `ef9c3f6e`'s docstring-retirement pass missed this file.)
 *
 * `clamp` exists for the comment stream only: `COMMENT_BODY_MAX_LENGTH` is
 * 16,384, and one comment must not push the next one off the screen. The
 * description caller (`FE-14.2`) simply never passes it.
 */

const ALLOWED_MARKDOWN_ELEMENTS = [
  'p',
  'br',
  'strong',
  'em',
  'del',
  'code',
  'pre',
  'a',
  'ul',
  'ol',
  'li',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'img',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'hr',
];

const COMMENT_MARKDOWN_ELEMENTS = [
  'p',
  'br',
  'strong',
  'em',
  'del',
  'code',
  'pre',
  'a',
  'ul',
  'ol',
  'li',
  'blockquote',
  'hr',
];

const MARKDOWN_COMPONENTS: Components = {
  // External by construction — every link in stored Markdown points away
  // from the app. `noopener noreferrer` is the standard pairing for
  // `target="_blank"`, not decoration.
  a: ({ href, children, node: _node, ...rest }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  ),
  img: ({ alt, node: _node, ...rest }) => (
    // `alt=""` rather than a missing attribute: an image whose author wrote no
    // alt text is decorative as far as we can tell, and an empty alt is how a
    // screen reader is told to skip it. A missing one makes it read the URL.
    // (`jsx-a11y` is not among this repository's ESLint plugins, so nothing
    // enforces that mechanically — it is a convention here, not a rule.)
    <img alt={alt ?? ''} loading="lazy" className="max-w-full rounded-card" {...rest} />
  ),
};

export type MarkdownBodyVariant = 'description' | 'comment';

export interface MarkdownBodyProps extends Omit<
  ComponentPropsWithoutRef<'div'>,
  'children'
> {
  markdown: string;
  variant?: MarkdownBodyVariant;
  /** Comment stream only. `maxHeight` in pixels. */
  clamp?: { maxHeight: number; moreLabel: string };
  mentionMembers?: readonly BoardMemberWithHistory[];
  currentUserId?: string | undefined;
  ref?: Ref<HTMLDivElement>;
}

export function MarkdownBody({
  markdown,
  variant = 'description',
  clamp,
  mentionMembers = [],
  currentUserId,
  className,
  ref,
  ...rest
}: MarkdownBodyProps) {
  const { t } = useInterfaceLanguage();
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isLimited = clamp !== undefined && !expanded;
  const commentComponents =
    variant === 'comment'
      ? commentMarkdownComponents(t, mentionMembers, currentUserId)
      : MARKDOWN_COMPONENTS;

  useLayoutEffect(() => {
    if (clamp === undefined || expanded) {
      setHasOverflow(false);
      return undefined;
    }

    const element = contentRef.current;
    if (element === null) return undefined;

    const measure = () => {
      setHasOverflow(element.scrollHeight > clamp.maxHeight + 1);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [clamp, expanded, markdown]);

  return (
    <div ref={ref} className={cn('font-sans text-card text-ink', className)} {...rest}>
      <div
        ref={contentRef}
        style={isLimited ? { maxHeight: clamp.maxHeight, overflow: 'hidden' } : undefined}
        className={cn(
          '[&_a]:text-accent [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-border-strong',
          '[&_blockquote]:pl-3 [&_blockquote]:text-ink-muted [&_code]:rounded-card [&_code]:bg-surface-raised',
          '[&_code]:px-1 [&_pre]:overflow-x-auto [&_pre]:rounded-card [&_pre]:bg-surface-raised [&_pre]:p-2',
          '[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_p]:my-2',
        )}
      >
        <ReactMarkdown
          remarkPlugins={
            variant === 'comment' ? [remarkGfm, remarkCommentLineBreaks] : [remarkGfm]
          }
          allowedElements={
            variant === 'comment' ? COMMENT_MARKDOWN_ELEMENTS : ALLOWED_MARKDOWN_ELEMENTS
          }
          components={variant === 'comment' ? commentComponents : MARKDOWN_COMPONENTS}
        >
          {variant === 'comment' ? normaliseCommentMarkdown(markdown) : markdown}
        </ReactMarkdown>
      </div>

      {clamp !== undefined && !expanded && hasOverflow ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1 font-medium text-accent hover:underline"
        >
          {clamp.moreLabel}
        </button>
      ) : null}
    </div>
  );
}

function normaliseCommentMarkdown(markdown: string): string {
  const lines = markdown.split('\n');
  const tableLines = new Set<number>();
  let inFence = false;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? '';
    if (/^\s*```/.test(line)) inFence = !inFence;
    if (inFence) continue;
    const next = lines[index + 1];
    if (next === undefined) continue;
    if (looksLikeTableHeader(line, next)) {
      tableLines.add(index);
      tableLines.add(index + 1);
      for (let row = index + 2; row < lines.length; row += 1) {
        const value = lines[row] ?? '';
        if (value.trim() === '' || !value.includes('|')) break;
        tableLines.add(row);
      }
    }
  }

  inFence = false;
  return lines
    .map((line, index) => {
      if (/^\s*```/.test(line)) {
        inFence = !inFence;
        return line;
      }
      if (inFence) return line;
      const withoutHeading = line.replace(/^(\s{0,3})#(?=\s)/, '$1\\#');
      return tableLines.has(index)
        ? withoutHeading.replaceAll('|', '\\|')
        : withoutHeading;
    })
    .join('\n');
}

function looksLikeTableHeader(line: string, next: string): boolean {
  return (
    line.includes('|') && /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(next)
  );
}

/**
 * `FR-COLLAB-004`'s `:shortcode:` emoji, resolved from the CANONICAL
 * vocabulary in `packages/shared/src/comment.ts` rather than from a map this
 * file owns (`FE-30.1`). The five keys and the grammar `/:([a-z0-9_+-]+):/gi`
 * used to live here, privately, where nothing else in the workspace — not
 * `apps/mcp`, not a second client — could agree with them; the glyph set is
 * unchanged by the move, at `COMMENT_EMOJI_SHORTCODE_VERSION` 1.
 *
 * ⚠ **It runs on RENDERED INLINE CHILDREN, and must stay there** — the
 * shared docstring's clause (c). `code` and `pre` are absent from
 * `commentMarkdownComponents`, so they fall through to `MARKDOWN_COMPONENTS`
 * and this pass never reaches them: `:tada:` inside backticks or a fence
 * stays literal, which is `FR-COLLAB-004` AC2's "no markdown interpreted
 * inside it". Now that the resolver is one shared function the tempting
 * simplification is to call it once on the source in
 * `normaliseCommentMarkdown` — that would silently eat shortcodes inside
 * code, with nothing red anywhere.
 */
function renderShortcodes(children: ReactNode): ReactNode {
  if (typeof children === 'string') return replaceCommentEmojiShortcodes(children);
  return Children.map(children, (child) =>
    typeof child === 'string' ? replaceCommentEmojiShortcodes(child) : child,
  );
}

function commentMarkdownComponents(
  t: MarkdownTranslate,
  mentionMembers: readonly BoardMemberWithHistory[],
  currentUserId: string | undefined,
): Components {
  const inline = (children: ReactNode) =>
    renderCommentInline(t, children, mentionMembers, currentUserId);
  return {
    ...MARKDOWN_COMPONENTS,
    p: ({ children, node: _node, ...rest }) => <p {...rest}>{inline(children)}</p>,
    li: ({ children, node: _node, ...rest }) => <li {...rest}>{inline(children)}</li>,
    blockquote: ({ children, node: _node, ...rest }) => (
      <blockquote {...rest}>{inline(children)}</blockquote>
    ),
    strong: ({ children, node: _node, ...rest }) => (
      <strong {...rest}>{inline(children)}</strong>
    ),
    em: ({ children, node: _node, ...rest }) => <em {...rest}>{inline(children)}</em>,
    del: ({ children, node: _node, ...rest }) => <del {...rest}>{inline(children)}</del>,
  };
}

function renderCommentInline(
  t: MarkdownTranslate,
  children: ReactNode,
  mentionMembers: readonly BoardMemberWithHistory[],
  currentUserId: string | undefined,
): ReactNode {
  if (mentionMembers.length === 0) return renderShortcodes(children);
  return Children.map(children, (child, childIndex) => {
    if (typeof child !== 'string') return child;
    return commentMentionSegments(
      replaceCommentEmojiShortcodes(child),
      mentionMembers,
    ).map((segment, index) => {
      if (segment.kind === 'text') {
        return <span key={`${childIndex.toString()}-text-${index}`}>{segment.text}</span>;
      }
      const self = currentUserId !== undefined && segment.member.id === currentUserId;
      return (
        <span
          key={`${childIndex.toString()}-mention-${index}-${segment.member.id}`}
          className={cn(
            'inline-flex items-center rounded-card border px-1 font-medium',
            self
              ? 'border-accent bg-accent/10 text-accent'
              : 'border-border-strong bg-surface text-ink',
          )}
          // Two whole messages, never a stem plus a `, you were mentioned`
          // suffix: `FR-EXP-028`'s edge case forbids assembling a sentence by
          // concatenation, because a suffix key freezes English clause order
          // and several languages both reorder the clause and inflect the
          // name for it. `{name}` is product data interpolated INTO chrome
          // and is never itself translated.
          aria-label={
            self
              ? t('primitives.markdown.mentionSelf', {
                  name: profileName(segment.member),
                })
              : t('primitives.markdown.mention', {
                  name: profileName(segment.member),
                })
          }
        >
          {segment.text}
        </span>
      );
    });
  });
}

interface MarkdownNode {
  type?: string;
  value?: string;
  children?: MarkdownNode[];
  [key: string]: unknown;
}

function remarkCommentLineBreaks() {
  return (tree: MarkdownNode) => {
    addHardBreaks(tree);
  };
}

function addHardBreaks(node: MarkdownNode): void {
  if (node.children === undefined) return;

  node.children = node.children.flatMap((child) => {
    addHardBreaks(child);
    if (child.type !== 'text' || !child.value?.includes('\n')) {
      return [child];
    }

    const parts = child.value.split('\n');
    return parts.flatMap((part, index) => {
      const next: MarkdownNode[] = [];
      if (part.length > 0) next.push({ ...child, value: part });
      if (index < parts.length - 1) next.push({ type: 'break' });
      return next;
    });
  });
}
